import { ethers, utils } from 'ethers';
import { dappStore, positionStore, userStore } from '../components/store';
import Contracts from '../components/eth/Contracts';
import * as ethValue from '../components/eth/StaticStore';
// import * as contractCalls from './ContractCalls';
import { setDebtAmount } from '../components/store/setters';
import { fetchOusdApy, fetchArchPrice } from '../components/eth/Fetch';
import { getOpenPositions } from '../components/eth/PositionInfo';
import { ERC20 } from 'types/ethers-contracts/ERC20';
import { ContractNames, TokenTypes } from 'types';
import { hardhat, mainnet } from '@components/eth/Addresses';
import { formatBigNumber } from 'utils/formatBigNumber';
import { useAccount, useNetwork, useProvider, useSigner } from 'wagmi';
import { formatUnits, parseUnits } from 'ethers/lib/utils.js';
import { capitalizeFirstLetter } from 'utils/capitalizeFirstLetter';
import { determineTokenDecimals } from 'utils/determineTokenDecimals';
import { determineTokenFeeInUsdDecimals } from 'utils/determineTokenFeeInUsdDecimals';
import { getOpenPositionId } from 'utils/getOpenPositionId';
import { toBigNumber } from 'utils/toBigNumber';
import { parseError } from 'utils/parseError';
import mixpanel from 'mixpanel-browser';

export const useWeb3Methods = () => {
  const { address, isConnected } = useAccount();
  const provider = useProvider();
  const { data: signer } = useSigner();
  const { chain } = useNetwork();

  const connectLoadProvider = async () => {
    try {
      checkIfWrongNetwork();
      updateAddresses();
      createEventTriggers();
      // console.log('[useWeb3Methods.js] connectLoadProvider(): complete');
    } catch (err) {
      console.error('connectLoadProvider err: ', err);
      dappStore.setState({ currentError: parseError(err) });
      throw err;
    }
  };

  const handlePageReload = () => {
    (window as any).location.reload();
  };

  const handleNetworkChange = (newNetwork: any, oldNetwork: any) => {
    // When a Provider makes its initial connection, it emits a "network"
    // event with a null oldNetwork along with the newNetwork. So, if the
    // oldNetwork exists, it represents a changing network
    if (oldNetwork) {
      (window as any).location.reload();
    }
  };

  const createEventTriggers = () => {
    try {
      const ethereum = (window as any).ethereum;
      // If no ethereum object crashes and doesnt load
      // For example safari without metamask installed
      if (!ethereum) return;
      ethereum.on('accountsChanged', handlePageReload);
      ethereum.on('disconnect', handlePageReload);
      ethereum.on('chainChanged', handlePageReload);
      ethereum.on('network', handleNetworkChange);
    } catch (err) {
      console.error('createEventTriggers err: ', err);
      dappStore.setState({ currentError: parseError(err) });
      throw err;
    }
  };

  const checkIfWrongNetwork = () => {
    if (chain?.id && chain?.id !== 1337 && chain?.id !== 31337 && chain?.id !== 1) {
      console.error('Wrong network ID: ' + chain?.id);
      dappStore.setState({ isWrongNetwork: true });
    }
  };

  const updateAddresses = () => {
    if (chain?.network === 'aws-fork') {
      dappStore.setState({ addresses: mainnet });
    } else {
      dappStore.setState({ addresses: mainnet });
    }
  };

  const loadContracts = async () => {
    // console.log('called loadContracts');
    try {
      // We need a provider so make sure we're already connected
      if (!isConnected) return;

      // If connected to mainnet already all contracts are loaded
      if (chain?.id === 1) {
        await getOnchainData();
        return;
      }
      // Initialize the Contracts class
      const contracts = dappStore.getState().contracts;
      const newContracts = new Contracts(signer);
      dappStore.setState({
        contracts: { ...contracts, ...newContracts },
        contractsLoaded: true,
      });
      // console.log('[useWeb3Methods.js] loadContracts(): complete');
    } catch (err) {
      console.error('loadContracts err: ', err);
      dappStore.setState({ currentError: parseError(err) });
      throw err;
    }
  };

  const updateUserBalances = async () => {
    try {
      // We must be connected to make these calls
      if (!isConnected) return;
      const contracts = dappStore.getState().contracts;
      const tokenContractsNotLoaded =
        !contracts.lvUSD || !contracts.ousd || !contracts.arch || !contracts.usdt || !contracts.usdc || !contracts.dai;
      if (!contracts || tokenContractsNotLoaded) return;
      // console.log('called updateUserBalances');

      // LVUSD
      const balance_lvusd = await getERC20Balance(contracts.lvUSD, address);
      // OUSD
      const balance_ousd = await getERC20Balance(contracts.ousd, address);
      // ARCH
      const balance_arch = await getERC20Balance(contracts.arch, address);
      // USDT
      const balanceUsdt = await getERC20Balance(contracts.usdt, address);
      // USDC
      const balanceUsdc = await getERC20Balance(contracts.usdc, address);
      // DAI
      const balanceDai = await getERC20Balance(contracts.dai, address);

      const balanceData = {
        balanceLvusd: formatBigNumber(balance_lvusd),
        balanceOusd: formatBigNumber(balance_ousd),
        balanceArch: formatBigNumber(balance_arch),
        balanceUsdt: Number(formatBigNumber(balanceUsdt, determineTokenDecimals('usdt'))),
        balanceUsdc: Number(formatBigNumber(balanceUsdc, determineTokenDecimals('usdc'))),
        balanceDai: Number(formatBigNumber(balanceDai, determineTokenDecimals('dai'))),
      };
      userStore.setState(balanceData);
      userStore.setState({
        balanceOusdIn18Dec: balance_ousd,
        balanceArchIn18Dec: balance_arch,
      });
      dappStore.setState({ balancesLoaded: true });

      //mixpanel
      mixpanel.people.set(balanceData);
      const formattedName = address.slice(0, 4) + '...' + address.slice(-4);
      mixpanel.people.set({ name: formattedName });

      // console.log('[useWeb3Methods.js] updateUserBalances(): complete');
    } catch (err) {
      console.error('updateUserBalances err: ', err);
      dappStore.setState({ currentError: parseError(err) });
      throw err;
    }
  };

  const loadParamStore = async () => {
    try {
      const contracts = dappStore.getState().contracts;
      const addresses = dappStore.getState().addresses;
      // Contracts must be already loaded
      if (!contracts?.parameterStore || !contracts?.lvUSD) {
        console.error('[useWeb3Methods.js] getOnchainContracts(): Error: Contracts not loaded');
        return;
      }

      // MAX LEVERAGE AVAILABLE
      const leverageAvailableIn18Dec = await contracts.coordinator.getAvailableLeverage();
      const leverageAvailable = formatBigNumber(leverageAvailableIn18Dec);
      // PARAM STORE
      const paramStore = contracts.parameterStore;
      const ousdCollateralMinimum = formatBigNumber(await paramStore.getMinPositionCollateral());
      const originationFee = formatBigNumber(await paramStore.getOriginationFeeRate());
      const performanceFee = formatBigNumber(await paramStore.getRebaseFeeRate());
      // LVUSD to ARCH ratio
      const lvUSDToArchRatioIn18Dec = await paramStore.getArchToLevRatio();
      const lvUSDToArchRatio = formatBigNumber(lvUSDToArchRatioIn18Dec);
      const positionLifetimeInDays = Number(formatUnits(await paramStore.getPositionTimeToLiveInDays(), 0));
      const minimumDepositIncreased = Number(ousdCollateralMinimum) * 10;

      dappStore.setState({
        ousdCollateralMinimum,
        originationFee,
        performanceFee,
        lvUSDToArchRatio,
        lvUSDToArchRatioIn18Dec,
        leverageAvailable,
        leverageAvailableIn18Dec,
        positionLifetimeInDays,
        minimumDepositIncreased,
      });

      // console.log('[useWeb3Methods.js] loadParamStore(): complete');
    } catch (err) {
      console.error('loadParamStore err: ', err);
      dappStore.setState({ currentError: parseError(err) });
      throw err;
    }
  };

  const getERC20Balance = async (token: ERC20, address: string) => {
    if (!token) return;
    try {
      return await token.balanceOf(address);
    } catch (err) {
      console.log('getERC20Balance err: ', err);
    }
  };

  const getERC20Allowance = async (token: ERC20, addressOwner: string, addressSpender: string) => {
    if (!token) return;
    try {
      return await token.allowance(addressOwner, addressSpender);
    } catch (err) {
      console.log('getERC20Allowance err: ', err);
    }
  };

  const updateUserPositions = async () => {
    try {
      const userAddress = userStore.getState().userAddress;
      const contracts = dappStore.getState().contracts;
      if (contracts?.positionToken) {
        const positions = await getOpenPositions(contracts, userAddress);
        userStore.setState({ positions });
      }
    } catch (err) {
      console.error('updateUserPositions err: ', err);
      dappStore.setState({ currentError: parseError(err) });
      throw err;
    }
  };

  const calculateDappSettings = async () => {
    const archToLevRatio = dappStore.getState().lvUSDToArchRatio;
    const numOfCycles = dappStore.getState().numOfCycles;
    const baseOusdApy = Number(dappStore.getState().ousdAPY);
    const performanceFee = 1 - Number(dappStore.getState().performanceFee);
    await setDebtAmount(1, numOfCycles, (leverageMultiplier: number) => {
      const leveragedOusdApy = baseOusdApy * (leverageMultiplier + 1);
      const finalOusdApy = leveragedOusdApy * performanceFee;
      dappStore.setState({ leverageMultiplier: leverageMultiplier });
      dappStore.setState({ archMultiplier: leverageMultiplier / Number(archToLevRatio) });
      dappStore.setState({ leveragedOusdApy: baseOusdApy * leverageMultiplier });
      dappStore.setState({ finalOusdApy: leveragedOusdApy * performanceFee });
    });
  };

  // Here we set various setIntervals. Careful not to set more than once!!!
  let intervalFetches = false;
  const setupIntervals = async () => {
    const oneBlockTime = 12000; // 12 seconds
    if (intervalFetches == false) {
      intervalFetches = true;
      // We fetch everything onload first time
      await getSetAll();
      // Update ArchPrice ~ every 4 blocks
      setInterval(async () => {
        const archPrice = await fetchArchPrice();
        dappStore.setState({ archPrice: archPrice });
      }, oneBlockTime * 4);
      // Update OUSD Apy ~ every 40 blocks
      setInterval(async () => {
        const ousdApy = await fetchOusdApy();
        dappStore.setState({ ousdAPY: ousdApy });
      }, oneBlockTime * 40);
    }
  };

  const getSetAll = async () => {
    try {
      const archPrice = await fetchArchPrice();
      const ousdAPY = await fetchOusdApy();
      dappStore.setState({ archPrice, ousdAPY });
    } catch (error) {
      console.error(error);
    }
    try {
      const duneResponse = await fetch('https://s3.us-west-2.amazonaws.com/archimedesfi-data-json-v1.0/dune.json');
      const duneData = await duneResponse.json();
      const lpAPY = duneData.apy;
      const tvl = duneData.tvl;
      dappStore.setState({ lpAPY, tvl });
    } catch (error) {
      console.error(error);
    }
  };

  const updateApprovalStatus = async () => {
    // console.log('called updateApprovalStatus');
    try {
      const contracts = dappStore.getState().contracts;
      const addresses = dappStore.getState().addresses;
      if (!contracts || !contracts.arch || !contracts.ousd || !contracts.usdt || !contracts.usdc || !contracts.dai)
        return;
      const zapperAddress = addresses.zapperAddress;
      const allowance_arch = await getERC20Allowance(contracts.arch, address, zapperAddress);
      const allowance_ousd = await getERC20Allowance(contracts.ousd, address, zapperAddress);
      dappStore.setState({ archApprovalLoaded: true });
      const approvedUsdt = await getERC20Allowance(contracts.usdt, address, zapperAddress);
      const approvedUsdc = await getERC20Allowance(contracts.usdc, address, zapperAddress);
      const approvedDai = await getERC20Allowance(contracts.dai, address, zapperAddress);
      dappStore.setState({ stableCoinApprovalsLoaded: true });

      positionStore.setState({
        approvedOusd: ethers.utils.formatUnits(allowance_ousd),
        approvedOusdIn18Dec: allowance_ousd,
        approvedArch: ethers.utils.formatUnits(allowance_arch),
        approvedArchIn18Dec: allowance_arch,
        approvedUsdt: ethers.utils.formatUnits(approvedUsdt, 6),
        approvedUsdc: ethers.utils.formatUnits(approvedUsdc, 6),
        approvedDai: ethers.utils.formatUnits(approvedDai),
      });

      // console.log('Allowances updated.', {
      //   approvedOusd: ethers.utils.formatUnits(allowance_ousd),
      //   approvedOusdIn18Dec: allowance_ousd,
      //   approvedArch: ethers.utils.formatUnits(allowance_arch),
      //   approvedArchIn18Dec: allowance_arch,
      //   approvedUsdt: ethers.utils.formatUnits(approvedUsdt, 6),
      //   approvedUsdc: ethers.utils.formatUnits(approvedUsdc, 6),
      //   approvedDai: ethers.utils.formatUnits(approvedDai),
      // });
    } catch (err) {
      console.error('updateApprovalStatus err: ', err);
      dappStore.setState({ currentError: parseError(err) });
      throw err;
    }
  };

  const getContractsWithAlchemy = () => {
    const addresses = dappStore.getState().addresses;
    const alchemyProvider = new ethers.providers.AlchemyProvider('homestead', process.env.NEXT_PUBLIC_ALCHEMY_API_KEY);
    if (signer) {
      return {
        parameterStore: new ethers.Contract(addresses.paramStoreAddress, ethValue.paramStoreAbi, signer),
        lvUSD: new ethers.Contract(addresses.lvUSDAddress, ethValue.lvUSDAbi, signer),
        zapper: new ethers.Contract(addresses.zapperAddress, ethValue.zapperAbi, signer),
        arch: new ethers.Contract(addresses.archTokenAddress, ethValue.erc20abi, signer),
        ousd: new ethers.Contract(addresses.ousdAddress, ethValue.erc20abi, signer),
        usdt: new ethers.Contract(addresses.usdtAddress, ethValue.erc20abi, signer),
        usdc: new ethers.Contract(addresses.usdcAddress, ethValue.erc20abi, signer),
        dai: new ethers.Contract(addresses.daiAddress, ethValue.erc20abi, signer),
        levEngine: new ethers.Contract(addresses.levEngineAddress, ethValue.LevEngineAbi, signer),
        positionToken: new ethers.Contract(addresses.positionTokenAddress, ethValue.PositionTokenAbi, signer),
        cdp: new ethers.Contract(addresses.cdpAddress, ethValue.cdpAbi, signer),
        vault: new ethers.Contract(addresses.vaultAddress, ethValue.VaultAbi, signer),
        coordinator: new ethers.Contract(addresses.coordinatorAddress, ethValue.coordinatorAbi, signer),
      };
    }
    return {
      parameterStore: new ethers.Contract(addresses.paramStoreAddress, ethValue.paramStoreAbi, alchemyProvider),
      lvUSD: new ethers.Contract(addresses.lvUSDAddress, ethValue.lvUSDAbi, alchemyProvider),
      zapper: new ethers.Contract(addresses.zapperAddress, ethValue.zapperAbi, alchemyProvider),
      arch: new ethers.Contract(addresses.archTokenAddress, ethValue.erc20abi, alchemyProvider),
      ousd: new ethers.Contract(addresses.ousdAddress, ethValue.erc20abi, alchemyProvider),
      usdt: new ethers.Contract(addresses.usdtAddress, ethValue.erc20abi, alchemyProvider),
      usdc: new ethers.Contract(addresses.usdcAddress, ethValue.erc20abi, alchemyProvider),
      dai: new ethers.Contract(addresses.daiAddress, ethValue.erc20abi, alchemyProvider),
      levEngine: new ethers.Contract(addresses.levEngineAddress, ethValue.LevEngineAbi, alchemyProvider),
      positionToken: new ethers.Contract(addresses.positionTokenAddress, ethValue.PositionTokenAbi, alchemyProvider),
      cdp: new ethers.Contract(addresses.cdpAddress, ethValue.cdpAbi, alchemyProvider),
      vault: new ethers.Contract(addresses.vaultAddress, ethValue.VaultAbi, alchemyProvider),
      coordinator: new ethers.Contract(addresses.coordinatorAddress, ethValue.coordinatorAbi, alchemyProvider),
    };
  };

  const getOnchainData = async () => {
    try {
      const addresses = dappStore.getState().addresses;
      const contracts = dappStore.getState().contracts;
      if (!chain?.id || chain?.id === 1) {
        const contracts = getContractsWithAlchemy();
        dappStore.setState({ contracts, contractsLoaded: true });
        // console.log('Loaded contracts from Alchemy');
      } else {
        // We are using hardhat so load paramstore and lvusd from local instances
        const parameterStore = new ethers.Contract(addresses.paramStoreAddress, ethValue.paramStoreAbi, provider);
        const lvUSD = new ethers.Contract(addresses.lvUSDAddress, ethValue.lvUSDAbi, provider);
        const coordinator = new ethers.Contract(addresses.coordinatorAddress, ethValue.coordinatorAbi, provider);
        dappStore.setState({
          contracts: { ...contracts, parameterStore, lvUSD, coordinator },
        });
        // console.log('Loaded contracts from local instances');
      }
      await loadParamStore();
      await setupIntervals();
      await calculateDappSettings();
    } catch (err) {
      console.error('getOnchainData err: ', err);
      dappStore.setState({ currentError: parseError(err) });
      throw err;
    }
  };

  const retrieveTokenFeeInUsd = async (
    token: TokenTypes,
    deposit: string,
    numOfCycles: number,
    stableCoinAddress: string,
  ) => {
    try {
      const contracts = dappStore.getState().contracts;
      const tokenDecimals = determineTokenDecimals(token);
      const usdDecimals = determineTokenFeeInUsdDecimals(token);
      const zapperContract = contracts.zapper;
      const res = await zapperContract.previewTokenSplit(
        utils.parseUnits(deposit, tokenDecimals),
        numOfCycles,
        stableCoinAddress,
      );
      return formatBigNumber(res[1], usdDecimals);
    } catch (err) {
      console.log('retrieveTokenFeeInUsd err: ', err);
    }
  };

  const retrieveTokenFeeInArch = async (
    token: TokenTypes,
    deposit: string,
    numOfCycles: number,
    stableCoinAddress: string,
    useOwnArch?: boolean,
  ) => {
    try {
      const contracts = dappStore.getState().contracts;
      const tokenDecimals = determineTokenDecimals(token);
      const zapperContract = contracts?.zapper;
      const response = await zapperContract.previewZapInAmount(
        toBigNumber(Number(deposit), tokenDecimals),
        toBigNumber(numOfCycles, 0),
        stableCoinAddress,
        useOwnArch,
      );
      return {
        collateral: formatBigNumber(response[0]),
        archTokenFeeInArch: formatBigNumber(response[1]),
      };
    } catch (err) {
      console.error('retrieveTokenFeeInArch err: ', err);
      dappStore.setState({ currentError: parseError(err) });
      throw err;
    }
  };

  const getTokenBalance = async (token: TokenTypes) => {
    try {
      const contracts = dappStore.getState().contracts;
      const address = userStore.getState().userAddress;
      const tokenContract = contracts[token];
      return await tokenContract.balanceOf(address);
    } catch (err) {
      console.error('getTokenBalance err: ', err);
      dappStore.setState({ currentError: parseError(err) });
      throw err;
    }
  };

  const getTokenAllowance = async (token: TokenTypes) => {
    try {
      const tokenDecimals = determineTokenDecimals(token);
      const contracts = dappStore.getState().contracts;
      const addresses = dappStore.getState().addresses;
      const address = userStore.getState().userAddress;
      const tokenContract = contracts[token];
      const allowance = formatBigNumber(await tokenContract.allowance(address, addresses.zapperAddress), tokenDecimals);
      return allowance;
    } catch (err) {
      console.error('getTokenAllowance err: ', err);
      dappStore.setState({ currentError: parseError(err) });
      throw err;
    }
  };

  const needsZeroApproval = (token: TokenTypes) => {
    const approvedUsdc = positionStore.getState().approvedUsdc;
    const approvedDai = positionStore.getState().approvedDai;
    const approvedUsdt = positionStore.getState().approvedUsdt;
    const approvedArch = positionStore.getState().approvedArch;
    if (token === 'usdc') return Number(approvedUsdc) > 0;
    if (token === 'dai') return Number(approvedDai) > 0;
    if (token === 'arch') return Number(approvedArch) > 0;
    return Number(approvedUsdt) > 0;
  };

  const approveToken = async (token: TokenTypes, amount: string) => {
    try {
      if (token === 'arch') {
        dappStore.setState({ archApprovalLoaded: false });
      } else {
        dappStore.setState({ stableCoinApprovalsLoaded: false });
      }
      const contracts = dappStore.getState().contracts;
      const addresses = dappStore.getState().addresses;
      const capitalizedTokenName = capitalizeFirstLetter(token);
      const tokenContract = contracts[token];
      const tokenDecimals = determineTokenDecimals(token);
      const amountToApprove = token === 'arch' ? Number(amount) * 1.01 : Number(amount);
      const bigNumberAmount = parseUnits(amountToApprove.toString(), tokenDecimals);
      if (needsZeroApproval(token)) {
        const tx = await tokenContract.approve(addresses.zapperAddress, '0');
        await tx.wait();
      }
      const tx = await tokenContract.approve(addresses.zapperAddress, bigNumberAmount);
      const res = await tx.wait();
      if (res) {
        positionStore.setState({
          [`is${capitalizedTokenName}Approved`]: true,
        });
      }
      return res;
    } catch (err: any) {
      if (token === 'arch') {
        dappStore.setState({ archApprovalLoaded: true });
      } else {
        dappStore.setState({ stableCoinApprovalsLoaded: true });
      }
      console.error('approveToken err: ,', err);
      console.error(err?.reason);
      if (err?.reason?.includes('rejected transaction')) {
        dappStore.setState({ currentError: { message: 'Transaction rejected by user.' } });
      }
      throw err;
    }
  };

  const openPosition = async (
    token: TokenTypes,
    amount: string,
    numOfCycles: number,
    archTokenFeeInArch: string,
    collateral: string,
    maxSlippageAllowed: number,
    stableCoinAddress: string,
    useOwnArch?: boolean,
  ) => {
    try {
      const tokenDecimals = determineTokenDecimals(token);
      const contracts = dappStore.getState().contracts;
      const zapperContract = contracts.zapper;
      const bigNumberAmount = toBigNumber(Number(amount), tokenDecimals);
      const bigNumberTokenFeeInArch = toBigNumber(Number(archTokenFeeInArch), determineTokenDecimals('arch'));
      const bigNumberCollateral = toBigNumber(Number(collateral), determineTokenDecimals('ousd'));
      const tx = await zapperContract.zapIn(
        bigNumberAmount,
        toBigNumber(numOfCycles, 0),
        bigNumberTokenFeeInArch,
        bigNumberCollateral,
        toBigNumber(maxSlippageAllowed, 0),
        stableCoinAddress,
        useOwnArch,
      );
      dappStore.setState({ isProcessingModalOpen: true, latestTx: tx.hash });
      const res = await tx.wait();
      const activePositionId = getOpenPositionId(res.logs);
      const openPositions = await getOpenPositions(contracts, address);
      const activePosition = openPositions[openPositions.length - 1];
      setTimeout(() => {
        if (activePositionId > -1)
          dappStore.setState({ activePositionId, activePosition, isProcessingModalOpen: false, isViewNft: true });
      }, 1500);
      //mixpanel
      mixpanel.track('PositionOpened', {
        nftid: activePositionId,
        tokenIn: token,
        amountIn: amount,
        cycles: numOfCycles,
        archTokenFeeInArch: archTokenFeeInArch,
        collateral: collateral,
        useOwnArch: useOwnArch,
      });
      updateUserPositions();
      updateUserBalances();
      updateApprovalStatus();
      return res;
    } catch (err: any) {
      console.log('openPosition err: ', err);
      dappStore.setState({ isProcessingModalOpen: false });
      if (err?.reason?.includes('rejected transaction')) {
        dappStore.setState({ currentError: { message: 'Transaction rejected by user.' } });
      } else {
        dappStore.setState({ currentError: parseError(err) });
      }
      throw err;
    }
  };

  return {
    createEventTriggers,
    connectLoadProvider,
    loadContracts,
    loadParamStore,
    updateAddresses,
    getOnchainData,
    updateUserBalances,
    updateUserPositions,
    updateApprovalStatus,
    retrieveTokenFeeInUsd,
    retrieveTokenFeeInArch,
    approveToken,
    getTokenAllowance,
    openPosition,
  };
};
